import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { Component as FilterIcon } from 'icons/icon-ui--filter.svg';
import { Component as CloseIcon } from 'icons/icon-ui--close.svg';
import MealTypes from 'models/MealTypes';
import { useCart } from 'contexts/Cart';
import { preferencesByTagName } from 'utils/constants';
import Nav from '../../../models/Nav';
import { BreakpointMedia, FeatureFlag } from '../../../config';
import useCurrentUser from '../../../hooks/useCurrentUser';
import { Programs } from '../../../models/Plan';
import PreferenceFilterSelection from './PreferenceFilterSelection';

type Props = {
  filter: string;
  subFilter: string;
  preferenceFilters: string[];
  showPreferenceFilterSelection: boolean;
  filteredResultCount: number;
  onFilterClick: (filterArg: string) => void;
  onSubFilterClick: (subFilterArg: string) => void;
  setPreferenceFilters: (preferenceFilters: string[]) => void;
  setShowPreferenceFilterSelection: (arg: boolean) => void;
};

function Navigation({
  filter,
  subFilter,
  preferenceFilters,
  showPreferenceFilterSelection,
  filteredResultCount,
  onFilterClick,
  onSubFilterClick,
  setPreferenceFilters,
  setShowPreferenceFilterSelection,
}: Props): ReactElement {
  const { cart } = useCart();
  const { currentUser } = useCurrentUser();

  const isBundlesEnabled = currentUser.hasFeatureEnabled(FeatureFlag.BUNDLES);
  const areProgramsEnabled = currentUser.hasFeatureEnabled(FeatureFlag.PROGRAMS);
  const isRteDaypartFiltersEnabled = currentUser.hasFeatureEnabled(FeatureFlag.RTE_DAYPART_FILTERS);

  const productsByMealType = useMemo(
    () => cart.productsByMealType,
    [cart],
  );
  const webFilters = currentUser?.subscription?.plan === Programs.JUMPSTART_PROGRAM
  && areProgramsEnabled
    ? Nav.webFilters
    : Nav.webFilters.filter((webFilter) => webFilter !== Nav.programs);
  // @ts-expect-error TODO: fix null safety
  const isBundlesAvailable = productsByMealType.bundle?.length > 0;

  const showPreferenceSelectionAsDrawer = !useMediaQuery({ query: BreakpointMedia.SM });
  const [isSubfilterOverflow, setIsSubfilterOverflow] = useState(false);

  const setActiveFilter = (e, mealType) => {
    e.preventDefault();
    onFilterClick(mealType);
    onSubFilterClick('');
    setPreferenceFilters([]);
    window.scrollTo(0, 0);
  };

  const setActiveSubFilter = (e, mealType) => {
    e.preventDefault();
    if (subFilter === mealType) {
      onSubFilterClick('');
    } else {
      onSubFilterClick(mealType);
    }
    window.scrollTo(0, 0);
  };

  const handlePreferenceFilterClick = (e) => {
    e.preventDefault();
    setShowPreferenceFilterSelection(!showPreferenceFilterSelection);
    window.scrollTo(0, 0);
  };

  const handleClickOutsideDrawer = (e) => {
    if (e.target.classList.contains('c-filter-offcanvas-overlay')) {
      setShowPreferenceFilterSelection(false);
    }
  };

  const removePreferenceFilter = (e, selectedPreference: string) => {
    e.preventDefault();
    setPreferenceFilters(preferenceFilters.filter((tag) => tag !== selectedPreference));
    window.scrollTo(0, 0);
  };

  const scrollToSection = (hash, scrollBehavior) => {
    if (hash) {
      const element = document.querySelector(hash);

      if (element) {
        element.scrollIntoView({ behavior: scrollBehavior });
      }
    }
  };

  useEffect(() => scrollToSection(window.location.hash, 'auto'), []);

  useEffect(() => {
    if (showPreferenceFilterSelection) {
      document.body.classList.add('no-scroll-y');
    } else {
      document.body.classList.remove('no-scroll-y');
    }

    return () => {
      document.body.classList.remove('no-scroll-y');
    };
  }, [showPreferenceFilterSelection]);

  // Checks if the subfilter buttons exceed screen width (AKA if the subfilters
  // are horizontally scrollable) for styling purposes
  useEffect(() => {
    const subfilterRow = document.getElementById('subfilter-row');
    // @ts-expect-error TODO: fix null safety
    setIsSubfilterOverflow(subfilterRow?.scrollWidth > subfilterRow?.clientWidth);
  }, [preferenceFilters]);

  return (
    <>
      {showPreferenceFilterSelection && (
        <div
          className="c-filter-offcanvas-overlay"
          role="none"
          onClick={(e) => handleClickOutsideDrawer(e)}
        >
          <PreferenceFilterSelection
            preferenceFilters={preferenceFilters}
            filteredResultCount={filteredResultCount}
            setPreferenceFilters={setPreferenceFilters}
            setShowPreferenceFilterSelection={setShowPreferenceFilterSelection}
          />
        </div>
      )}
      <div className="c-header__sub">
        <div className="c-tab-container">
          <ul className="c-nav c-nav--tabs">
            {webFilters.map((nav) => (
              <li key={nav.key} className="c-nav__item nav-item">
                <a
                  href={`#${nav.key}`}
                  className={[
                    'c-nav__link',
                    'nav-link',
                    filter === nav.key ? 'active' : '',
                    nav.heapEvent,
                  ].join(' ')}
                  onClick={(e) => {
                    setActiveFilter(e, nav.key);
                  }}
                >
                  {nav.webLabel}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {filter !== Nav.programs.key && (
        <div className="c-header__sub">
          <div className="c-tab-container">
            <ul
              className={[
                'c-nav',
                'c-nav--tabs',
                isSubfilterOverflow ? 'justify-content-start' : '',
              ].join(' ')}
              id="subfilter-row"
            >
              <li key="preferences" className="c-nav__item nav-item">
                <a
                  role="button"
                  href="#preferenceFilters"
                  className={[
                    'c-nav__pill',
                    'nav-link',
                    preferenceFilters?.length ? 'active' : '',
                  ].join(' ')}
                  onClick={(e) => handlePreferenceFilterClick(e)}
                >
                  <div className="c-nav__preferences-filter">
                    <FilterIcon />
                    Filter
                    {preferenceFilters?.length > 0 && (
                      <>
                        <div className="c-nav__preferences-filter-count" />
                        {preferenceFilters.length}
                      </>
                    )}
                  </div>
                </a>
              </li>
              {preferenceFilters.map((tag) => (
                <li key={tag} className="c-nav__item nav-item">
                  <a
                    href={`#${tag}`}
                    className="c-nav__pill nav-link active"
                    onClick={(e) => removePreferenceFilter(e, tag)}
                  >
                    <CloseIcon width={20} height={20} />
                    {' '}
                    {preferencesByTagName[tag]}
                  </a>
                </li>
              ))}
              {Nav.getWebSubFilters(isRteDaypartFiltersEnabled)[filter].map((nav) => (
                <li key={nav.key} className="c-nav__item nav-item">
                  <a
                    href={`#${nav.key}`}
                    className={[
                      'c-nav__pill',
                      'nav-link',
                      subFilter === nav.key ? 'active' : '',
                      nav.heapEvent,
                    ].join(' ')}
                    onClick={(e) => {
                      setActiveSubFilter(e, nav.key);
                    }}
                  >
                    {subFilter === nav.key && (
                      <>
                        <CloseIcon width={20} height={20} />
                        {' '}
                      </>
                    )}
                    {nav.webLabel}
                  </a>
                </li>
              ))}
              {(filter === 'view_all' && isBundlesEnabled && isBundlesAvailable) && (
                <li key="bundle" className="c-nav__item nav-item speero_bundles d-none">
                  <a
                    href="#bundles"
                    className={[
                      'c-nav__pill',
                      'nav-link',
                      subFilter === 'bundle' ? 'active' : '',
                      MealTypes.bundle.heapEvent,
                    ].join(' ')}
                    onClick={(e) => {
                      setActiveSubFilter(e, 'bundle');
                    }}
                  >
                    {subFilter === 'bundle' && (
                      'X '
                    )}
                    {Nav.bundle.webLabel}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {(filter === 'grocery' && isBundlesEnabled && isBundlesAvailable) && (

        <div className="c-header__sub speero_bundles d-none">
          <div className="c-tab-container">
            <ul className="c-nav c-nav--tabs">
              <li key="bundle" className="c-nav__item nav-item">
                <a
                  href="#bundles"
                  className={[
                    'c-nav__pill',
                    'nav-link',
                    subFilter === 'bundle' ? 'active' : '',
                    MealTypes.bundle.heapEvent,
                  ].join(' ')}
                  onClick={(e) => {
                    setActiveSubFilter(e, 'bundle');
                  }}
                >
                  {subFilter === 'bundle' && (
                    'X '
                  )}
                  {Nav.bundle.webLabel}
                </a>
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

export default Navigation;
